import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface BonusSettings {
  id: string;
  half_year_bonus_pool: number;
  require_approval: boolean;
  module_performance_enabled: boolean;
  module_fahrzeuglager_enabled: boolean;
}

export interface BonusCriteria {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
}

export interface Trade {
  id: string;
  name: string;
}

export interface TargetThreshold {
  percent: number;
  amount: number;
}

export interface CriteriaThreshold {
  min_points: number;
  bonus_percent: number;
}

export interface BonusTradeSettings {
  id: string;
  trade_id: string;
  target_thresholds: TargetThreshold[];
  criteria_thresholds: CriteriaThreshold[];
}

interface BonusSettingsContextType {
  settings: BonusSettings | null;
  loading: boolean;
  criteria: BonusCriteria[];
  trades: Trade[];
  tradeSettings: BonusTradeSettings[];
  updateSettings: (s: Partial<BonusSettings>) => Promise<void>;
  calculateBonus: (tradeId: string | null, targetPercent: number, criteriaPoints: number) => { baseBonus: number; multiplier: number; finalBonus: number };
  getResult: (totalScore: number) => 'bonus' | 'neutral' | 'improvement';
  // CRUD criteria
  addCriteria: (name: string, description?: string) => Promise<void>;
  updateCriteria: (id: string, updates: Partial<BonusCriteria>) => Promise<void>;
  deleteCriteria: (id: string) => Promise<void>;
  // CRUD trades
  addTrade: (name: string) => Promise<void>;
  updateTrade: (id: string, name: string) => Promise<void>;
  deleteTrade: (id: string) => Promise<void>;
  // CRUD trade settings
  saveTradeSettings: (tradeId: string, targetThresholds: TargetThreshold[], criteriaThresholds: CriteriaThreshold[]) => Promise<void>;
  refetchAll: () => Promise<void>;
}

const BonusSettingsContext = createContext<BonusSettingsContextType | undefined>(undefined);

export const BonusSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<BonusSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [criteria, setCriteria] = useState<BonusCriteria[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [tradeSettings, setTradeSettings] = useState<BonusTradeSettings[]>([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [settingsRes, criteriaRes, tradesRes, tradeSettingsRes] = await Promise.all([
      supabase.from('bonus_settings').select('*').limit(1).single(),
      supabase.from('bonus_criteria').select('*').order('display_order'),
      supabase.from('trades').select('*').order('name'),
      supabase.from('bonus_trade_settings').select('*'),
    ]);

    if (settingsRes.data) {
      const d = settingsRes.data;
      setSettings({
        id: d.id,
        half_year_bonus_pool: Number(d.half_year_bonus_pool),
        require_approval: (d as any).require_approval ?? true,
        module_performance_enabled: (d as any).module_performance_enabled ?? true,
        module_fahrzeuglager_enabled: (d as any).module_fahrzeuglager_enabled ?? true,
      });
    }

    setCriteria((criteriaRes.data || []).map((c: any) => ({
      id: c.id,
      name: c.name,
      description: c.description,
      display_order: c.display_order,
    })));

    setTrades((tradesRes.data || []).map((t: any) => ({ id: t.id, name: t.name })));

    setTradeSettings((tradeSettingsRes.data || []).map((ts: any) => ({
      id: ts.id,
      trade_id: ts.trade_id,
      target_thresholds: (ts.target_thresholds || []) as TargetThreshold[],
      criteria_thresholds: (ts.criteria_thresholds || []) as CriteriaThreshold[],
    })));

    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateSettings = async (updates: Partial<BonusSettings>) => {
    if (!settings) return;
    const { id, ...rest } = updates;
    await supabase
      .from('bonus_settings')
      .update({ ...rest, updated_at: new Date().toISOString() } as any)
      .eq('id', settings.id);
    setSettings(prev => prev ? { ...prev, ...updates } : prev);
  };

  const calculateBonus = (tradeId: string | null, targetPercent: number, criteriaPoints: number) => {
    if (!tradeId) return { baseBonus: 0, multiplier: 100, finalBonus: 0 };
    const ts = tradeSettings.find(t => t.trade_id === tradeId);
    if (!ts) return { baseBonus: 0, multiplier: 100, finalBonus: 0 };

    // Sort descending by percent, find highest matching
    const sortedTarget = [...ts.target_thresholds].sort((a, b) => b.percent - a.percent);
    const matchedTarget = sortedTarget.find(t => targetPercent >= t.percent);
    const baseBonus = matchedTarget?.amount ?? 0;

    // Sort descending by min_points, find highest matching
    const sortedCriteria = [...ts.criteria_thresholds].sort((a, b) => b.min_points - a.min_points);
    const matchedCriteria = sortedCriteria.find(c => criteriaPoints >= c.min_points);
    const multiplier = matchedCriteria?.bonus_percent ?? 0;

    const finalBonus = baseBonus * (multiplier / 100);
    return { baseBonus, multiplier, finalBonus };
  };

  const getResult = (finalBonus: number): 'bonus' | 'neutral' | 'improvement' => {
    if (finalBonus > 0) return 'bonus';
    return 'neutral';
  };

  // CRUD Criteria
  const addCriteria = async (name: string, description?: string) => {
    const maxOrder = criteria.length > 0 ? Math.max(...criteria.map(c => c.display_order)) : 0;
    await supabase.from('bonus_criteria').insert({ name, description: description || null, display_order: maxOrder + 1 } as any);
    await fetchAll();
  };
  const updateCriteria = async (id: string, updates: Partial<BonusCriteria>) => {
    await supabase.from('bonus_criteria').update(updates as any).eq('id', id);
    await fetchAll();
  };
  const deleteCriteria = async (id: string) => {
    await supabase.from('bonus_criteria').delete().eq('id', id);
    await fetchAll();
  };

  // CRUD Trades
  const addTrade = async (name: string) => {
    const { data } = await supabase.from('trades').insert({ name } as any).select('id').single();
    if (data) {
      // Auto-create empty trade settings
      await supabase.from('bonus_trade_settings').insert({ trade_id: data.id, target_thresholds: [], criteria_thresholds: [] } as any);
    }
    await fetchAll();
  };
  const updateTrade = async (id: string, name: string) => {
    await supabase.from('trades').update({ name } as any).eq('id', id);
    await fetchAll();
  };
  const deleteTrade = async (id: string) => {
    await supabase.from('trades').delete().eq('id', id);
    await fetchAll();
  };

  // Save trade settings
  const saveTradeSettings = async (tradeId: string, targetThresholds: TargetThreshold[], criteriaThresholds: CriteriaThreshold[]) => {
    const existing = tradeSettings.find(ts => ts.trade_id === tradeId);
    if (existing) {
      await supabase.from('bonus_trade_settings').update({
        target_thresholds: targetThresholds as any,
        criteria_thresholds: criteriaThresholds as any,
        updated_at: new Date().toISOString(),
      } as any).eq('id', existing.id);
    } else {
      await supabase.from('bonus_trade_settings').insert({
        trade_id: tradeId,
        target_thresholds: targetThresholds as any,
        criteria_thresholds: criteriaThresholds as any,
      } as any);
    }
    await fetchAll();
  };

  return (
    <BonusSettingsContext.Provider value={{
      settings, loading, criteria, trades, tradeSettings,
      updateSettings, calculateBonus, getResult,
      addCriteria, updateCriteria, deleteCriteria,
      addTrade, updateTrade, deleteTrade,
      saveTradeSettings, refetchAll: fetchAll,
    }}>
      {children}
    </BonusSettingsContext.Provider>
  );
};

export const useBonusSettings = () => {
  const context = useContext(BonusSettingsContext);
  if (!context) throw new Error('useBonusSettings must be used within BonusSettingsProvider');
  return context;
};
